<script setup lang="ts">
/**
 * The way back to a speaking room from the player (job #752). Shown only on
 * the resting screen, never during playback — the course is private and the
 * room must not sit on top of it. It reads the device's pointer to the open
 * session; the room itself is the truth and corrects the pointer on every read.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n, getLanguageName } from '@/composables/useI18n'
import { activeSpeaking } from '@/composables/useSpeakingRooms'

const props = defineProps<{ courseTargetLang: string | null | undefined }>()
const { t } = useI18n()
const router = useRouter()

const counts = computed(() => !props.courseTargetLang || props.courseTargetLang === activeSpeaking.value?.targetLang)
const lang = computed(() => getLanguageName(activeSpeaking.value?.targetLang ?? ''))
</script>

<template>
  <div v-if="activeSpeaking" class="chip-wrap">
    <button class="chip" data-test="speak-chip" @click="router.push(`/rooms/${activeSpeaking.roomId}`)">
      <span class="dot" aria-hidden="true"></span>
      <span v-if="counts">{{ t('rooms.chipIn', 'In your speaking room · back to the room') }}</span>
      <span v-else>{{ t('rooms.chipOther', 'Only {language} counts in your room · back to the room').replace('{language}', lang) }}</span>
    </button>
  </div>
</template>

<style scoped>
.chip-wrap {
  position: fixed;
  left: 0;
  right: 0;
  top: calc(10px + env(safe-area-inset-top, 0px));
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 40;
  padding: 0 max(12px, env(safe-area-inset-left, 0px)) 0 max(12px, env(safe-area-inset-right, 0px));
}
.chip {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: var(--ink-primary, #2C2622);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
}
.chip span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dot { width: 8px; height: 8px; border-radius: 50%; background: #2f9e44; flex: none; }
</style>
